import {
  type IdTokenClaims,
  User,
  UserManager,
  WebStorageStateStore,
} from 'oidc-client-ts';

/**
 * OIDC-клиент сервиса «Промышленный туризм». Конфигурация повторяет
 * lk-web/quotas-web — единый стиль логина для всех приложений платформы.
 *
 * Регистрация нового пользователя — НЕ через Keycloak, а нативной формой
 * (RegisterPage → POST /api/v1/auth/register создаёт учётку через Admin API).
 * После регистрации пользователь входит обычным OIDC-логином (login_hint
 * подставляет email).
 */
const userManager = new UserManager({
  authority: `${import.meta.env.VITE_KEYCLOAK_URL}/realms/${import.meta.env.VITE_KEYCLOAK_REALM}`,
  client_id: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
  redirect_uri: `${window.location.origin}/auth/callback`,
  post_logout_redirect_uri: window.location.origin,
  response_type: 'code',
  scope: 'openid profile email',
  loadUserInfo: true,
  automaticSilentRenew: true,
  silent_redirect_uri: `${window.location.origin}/auth/silent-renew.html`,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
});

export { userManager };
export type { User };

export function startLogin(returnTo?: string, loginHint?: string): Promise<void> {
  return userManager.signinRedirect({
    state: returnTo ? { returnTo } : undefined,
    extraQueryParams: loginHint ? { login_hint: loginHint } : undefined,
  });
}

export interface RawTokens {
  access_token: string;
  refresh_token: string;
  id_token: string;
  token_type: string;
  expires_in: number;
  scope: string;
  session_state?: string;
}

/** UTF-8-безопасное декодирование payload JWT (в id_token — кириллические ФИО). */
function decodeJwtPayload(token: string): Record<string, unknown> {
  const base64 = (token.split('.')[1] ?? '').replace(/-/g, '+').replace(/_/g, '/');
  const json = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join(''),
  );
  return JSON.parse(json) as Record<string, unknown>;
}

/**
 * Сохранить готовые токены (полученные бэком через ROPC сразу после
 * регистрации) как активную OIDC-сессию — без редиректа на форму Keycloak.
 */
export async function storeTokens(t: RawTokens): Promise<User> {
  const profile = decodeJwtPayload(t.id_token) as unknown as IdTokenClaims;
  const user = new User({
    access_token: t.access_token,
    refresh_token: t.refresh_token,
    id_token: t.id_token,
    token_type: t.token_type,
    scope: t.scope,
    profile,
    expires_at: Math.floor(Date.now() / 1000) + t.expires_in,
    session_state: t.session_state ?? null,
  });
  await userManager.storeUser(user);
  return user;
}

export async function completeLogin(): Promise<{ user: User; returnTo: string }> {
  const user = await userManager.signinRedirectCallback();
  const state = user.state as { returnTo?: string } | undefined;
  return { user, returnTo: state?.returnTo ?? '/app' };
}

export function startLogout(): Promise<void> {
  return userManager.signoutRedirect();
}

export function getStoredUser(): Promise<User | null> {
  return userManager.getUser();
}
